import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { determineFlagPurposeOptions } from '@/helpers/determineFlagPurposeOptions';
import { trimFileNames } from '@/helpers/trimFileNames';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetDocumentByAccountAndDocumentIdQuery } from '@/services/documents';
import { isErrorWithData } from '@/services/helpers';
import {
  Box,
  Button,
  FormikSelectInputNative,
  FormikTextareaInput,
  Icon,
  Modal,
  SelectInput,
  toast,
  useBreakpoint,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMatch, useParams } from 'react-router-dom';

import {
  FlagCriteriaFilterType,
  FlagDocumentTypes,
  FlagProblem,
  FlagPurposeType,
  FlagReason,
  FlagStatus,
  MilestonePackageRoute,
  PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES,
  QueueStatus,
  RequirementAcceptanceCriterion,
  RequirementFlagSettingsDocument,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

import { useUploadDocumentMutation } from '../../services/documents';
import { useGetRequirementFlagSettingsQuery } from '../../services/requirementFlagSettings';
import RequirePermissions from '../auth/requirePermissions';

import { FlagPurposeSelectionPartial } from './FlagPurposeSelectionPartial';
import { QueueFlagsContext } from './QueueFlagsContext';

function AttachedDocItem({
  documentId,
  accountId,
  onRemove,
  isLocked,
}: {
  documentId: string;
  accountId: string;
  onRemove: (id: string) => void;
  isLocked: boolean;
}) {
  const { data: doc } = useGetDocumentByAccountAndDocumentIdQuery({ accountId, documentId });
  const handleRemove = useCallback(() => onRemove(documentId), [onRemove, documentId]);
  if (!doc) return null;

  return (
    <Box direction="row" alignItems="center" gap="xs" fontSize="sm">
      <Icon name="paperclip" color="body-secondary" size="sm" />
      <Box flex="auto">{doc.name || doc.files?.map((f) => f.originalName).join(', ')}</Box>
      {!isLocked && (
        <Button
          as="button"
          type="button"
          variant="tertiary"
          tone="neutral"
          size="sm"
          onClick={handleRemove}
          aria-label="Remove attached document"
        >
          <Icon name="c-remove" size="sm" />
        </Button>
      )}
    </Box>
  );
}

interface NewAddFlagModalProps {
  accountId: string;
  isOpen: boolean;
  onDismiss: () => void;
  dataKey: string;
  fileName?: string;
  baseCategory?: string;
  showUploadButton?: boolean;
  type?: string;
  flagType?: RequirementFlagSettingsType;
  editMode?: boolean;
  flagId?: string;
  queueStatus?: QueueStatus;
}

export const NewAddFlagModal = ({
  accountId,
  isOpen,
  onDismiss,
  dataKey,
  fileName,
  baseCategory,
  showUploadButton,
  type,
  flagType,
  editMode,
  flagId,
  queueStatus,
}: NewAddFlagModalProps) => {
  const { id } = useParams<{ id: any }>();
  const { data: account, isLoading: accountIsLoading } = useGetAccountQuery(id);
  const { addFlag, getFlagsByFlagType, isLoading, queue, resolveFlag, showResolveModal, updateFlag } =
    useContext(QueueFlagsContext);
  const [flagFiles, setFlagFiles] = useState<FileList | undefined>();
  const [selectedCriterion, setSelectedCriterion] = useState<{ label: string; value: string } | null>();
  const [attachedDocumentIds, setAttachedDocumentIds] = useState<string[]>([]);
  const isDecisionRendered =
    queueStatus === QueueStatus.approved || queueStatus === QueueStatus.conditionallyApproved;
  const [uploadDocument] = useUploadDocumentMutation();
  const { isPhone } = useBreakpoint();
  const { data: requirementFlagSettings } = useGetRequirementFlagSettingsQuery(
    { type: flagType },
    { skip: !flagType },
  );
  const milestonePackage = useMatch('/accounts/:id/:milestonePackage/*');
  const currentMilestonePackage = milestonePackage?.params.milestonePackage as MilestonePackageRoute;
  const currentFlags = useMemo(
    () => (flagType ? getFlagsByFlagType(flagType, false, dataKey, fileName) : []),
    [flagType, getFlagsByFlagType, dataKey, fileName],
  );
  const setting: RequirementFlagSettingsDocument | undefined = requirementFlagSettings?.requirementFlagSettings?.[0];

  const accountState = queue?.account?.address?.state || '';

  const currentCriterionIdsInUse = useMemo(
    () => currentFlags?.map((flag) => flag?.problems && flag?.problems[0]?.acceptanceCriteriaId?.toString()) || [],
    [currentFlags],
  );
  const canDeleteFlag = queueStatus && PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES.has(queueStatus);

  const selectedFlagToEdit = useMemo(() => {
    return currentFlags?.find((flag) => flag.id?.toString() === flagId);
  }, [currentFlags, flagId]);

  const availableCriteria = useMemo(() => {
    // If there is a criteria filter configured, process that first...

    const criteria = setting?.isFilterable
      ? setting?.acceptanceCriteria?.filter((criterion) => {
          const filters = criterion?.filters || [];
          const filterMatches = filters?.map((filter) => {
            switch (filter.type) {
              case FlagCriteriaFilterType.addressState:
                return filter.value === accountState;
              default:
                return false;
            }
          });
          return filterMatches?.length > 0 ? filterMatches.every((match) => match) : true; // If no filters applied to the criterion, include it as an option.
        })
      : setting?.acceptanceCriteria;

    if (editMode && selectedFlagToEdit?.problems?.length) {
      const currentCriterionId = selectedFlagToEdit?.problems[0]?.acceptanceCriteriaId?.toString();
      return (
        criteria?.filter(
          (criterion) =>
            !currentCriterionIdsInUse.includes(criterion.id.toString()) ||
            criterion.id.toString() === currentCriterionId,
        ) || []
      );
    }

    return criteria?.filter((criterion) => !currentCriterionIdsInUse.includes(criterion.id.toString())) || [];
  }, [editMode, selectedFlagToEdit?.problems, setting, currentCriterionIdsInUse]);

  const flagCriteriaOrDefaultReasonsAvailable = !setting?.acceptanceCriteria?.length || availableCriteria.length;

  const potentialCriteriaProblems = useMemo(() => {
    return (
      setting?.acceptanceCriteria?.reduce((acc: Record<string, any>, criterion: RequirementAcceptanceCriterion) => {
        const idAsString = criterion.id.toString();
        if (
          !currentCriterionIdsInUse.includes(idAsString) ||
          (editMode &&
            selectedFlagToEdit?.problems?.length &&
            selectedFlagToEdit?.problems[0]?.acceptanceCriteriaId?.toString() === idAsString)
        ) {
          acc[idAsString] = [];
        }
        return acc;
      }, {}) || {}
    );
  }, [setting?.acceptanceCriteria, currentCriterionIdsInUse, editMode, selectedFlagToEdit]);

  useEffect(() => {
    if (editMode && flagId && isOpen) {
      const flagFromQueue = queue?.flags?.find((f: any) => f.id?.toString() === flagId);
      setAttachedDocumentIds(flagFromQueue?.attachedDocumentIds ?? []);
    }
    if (editMode && selectedFlagToEdit && isOpen) {
      if (selectedFlagToEdit.problems?.length) {
        const criterionId = selectedFlagToEdit.problems[0]?.acceptanceCriteriaId?.toString();
        const criterion = setting?.acceptanceCriteria?.find((c) => c.id.toString() === criterionId);

        if (criterion) {
          setSelectedCriterion({
            value: criterionId,
            label: criterion.displayText,
          });
        }
      }
    }
  }, [editMode, flagId, queue?.flags, selectedFlagToEdit, isOpen, setting?.acceptanceCriteria]);

  const handleFileUpload = useCallback((event: any) => {
    setFlagFiles(event.target.files);
  }, []);

  const handleRemoveAttachment = useCallback((docId: string) => {
    setAttachedDocumentIds((prev) => prev.filter((id) => id !== docId));
  }, []);
  const reasonOptions = useMemo(() => {
    return Object.values(FlagReason).map((value) => ({ value: value, label: value }));
  }, []);

  const purposeOptions = useMemo(
    () => determineFlagPurposeOptions(account, currentMilestonePackage, editMode),
    [account, currentMilestonePackage, editMode],
  );

  const selectedCriterionOptions = useMemo(() => {
    const options = setting?.acceptanceCriteria
      ?.find((criterion) => criterion?.id.toString() === selectedCriterion?.value)
      ?.problems?.map((problem) => {
        if (problem?.details && problem?.details?.length > 0) {
          return problem.details.map((detail) => ({
            label: `${problem?.displayText} - ${detail}`,
            value: `${problem?.id.toString()}-${detail}`,
          }));
        } else {
          return {
            label: problem?.displayText,
            value: problem?.id.toString(),
          };
        }
      });
    return options?.flat() || [];
  }, [selectedCriterion, setting?.acceptanceCriteria]);

  const getInitialSelectedProblems = useMemo(() => {
    if (editMode && selectedFlagToEdit?.problems?.length && selectedCriterion?.value) {
      const problemsForCriterion = selectedFlagToEdit.problems.filter(
        (p) => p.acceptanceCriteriaId?.toString() === selectedCriterion.value,
      );

      return problemsForCriterion.flatMap((problem) => {
        if (problem.details?.length) {
          return problem.details.map((detail) => ({
            label: `${problem.displayText} - ${detail}`,
            value: `${problem.id.toString()}-${detail}`,
          }));
        } else {
          return {
            label: problem.displayText,
            value: problem.id.toString(),
          };
        }
      });
    }
    return [];
  }, [editMode, selectedFlagToEdit, selectedCriterion]);

  const handleHide = useCallback(() => {
    onDismiss();
    setFlagFiles(undefined);
    setSelectedCriterion(null);
    setAttachedDocumentIds([]);
  }, [onDismiss]);

  const handleDelete = useCallback(async () => {
    if (isLoading) return;

    if (window.confirm('Are you sure you want to delete this flag? This action cannot be undone.')) {
      try {
        await resolveFlag(dataKey, FlagStatus.closedDeleted, fileName, flagId);
        handleHide();
      } catch (e) {
        if (isErrorWithData(e)) {
          toast.error(e.data.message);
        } else {
          toast.error('An error occurred while deleting the flag');
        }
      }
    }
  }, [resolveFlag, dataKey, fileName, handleHide, isLoading, flagId]);

  const handleSubmit = useCallback(
    async (
      values: any,
      { setFieldError }: { setFieldError: (field: string, message: string | undefined) => void },
    ) => {
      if (isLoading) {
        return;
      }
      const currentPurpose = values.purpose as FlagPurposeType;
      const purposeIsValid = purposeOptions.some((opt) => opt.value === currentPurpose);
      if (!purposeIsValid) {
        toast.error(
          'The selected purpose type is not allowed for this milestone package. Please choose a different one',
        );
        return;
      }

      let uploadedDocumentId = '';

      let problems = [] as FlagProblem[];
      Object.keys(potentialCriteriaProblems).forEach((key) => {
        const criteriaProblems = values[key];
        if (Array.isArray(criteriaProblems) && criteriaProblems?.length > 0) {
          criteriaProblems.forEach((problem: { label: string; value: string }) => {
            // Need to transform multiple detail selections into single problem by problem id
            if (problem.value.includes('-')) {
              const labelParts = problem.label.split(' - ');
              const valueParts = problem.value.split('-');
              const problemLabel = labelParts[0];
              const problemId = valueParts[0];
              const detail = valueParts[1];
              const problemById = problems.find((p) => p.id === problemId);

              // Has this problem already been added by means of another detail selection?
              if (problemById) {
                const otherProblems = problems.filter((p) => p.id !== problemId);
                const updatedProblem = {
                  id: problemById.id,
                  displayText: problemById.displayText,
                  ...(problemById?.details?.length
                    ? { details: [...problemById.details, detail] }
                    : { details: [detail] }),
                  acceptanceCriteriaId: key,
                };
                problems = [...otherProblems, updatedProblem];
              } else {
                // Add the problem for the first time with its detail
                problems.push({
                  id: problemId,
                  displayText: problemLabel,
                  details: [detail],
                  acceptanceCriteriaId: key,
                });
              }
            } else {
              problems.push({
                id: problem.value,
                displayText: problem.label,
                acceptanceCriteriaId: key,
              });
            }
          });
        }
      });

      if (problems.length === 0 && !values.reason) {
        setFieldError('selectAcceptanceCriteria', 'At least one reason or problem selection is required');
        return;
      }

      try {
        if (flagFiles && flagFiles.length) {
          const data = new FormData();
          data.append('type', FlagDocumentTypes.flag);

          for (let i = 0; i < flagFiles.length; i++) {
            data.append(`files[${i}]`, flagFiles[i]);
          }

          const uploadedDocumentResult = (await uploadDocumentsWithToast({
            uploadDocument,
            accountId,
            formData: data,
            invalidateAllTags: false,
            files: flagFiles,
          })) as any;
          uploadedDocumentId = uploadedDocumentResult?.documents?.[0]?.id ?? '';
        }

        const flagData = {
          dataKey,
          fileName,
          note: values.note,
          ...(values?.reason ? { reason: values.reason as FlagReason } : {}),
          ...(problems.length > 0 ? { problems } : {}),
          purposeType: values.purpose as FlagPurposeType,
          baseCategory,
          ...(uploadedDocumentId ? { fileUpload: uploadedDocumentId } : {}),
          type,
          flagType,
          ...(editMode && !isDecisionRendered ? { attachedDocumentIds } : {}),
        };

        if (editMode && flagId) {
          await updateFlag(flagId, flagData);
          toast.success('Flag updated successfully');
        } else {
          await addFlag(flagData);
          toast.success('Flag created successfully');
        }
        setSelectedCriterion(null);
        handleHide();
      } catch {
        // Upload or flag-save errored — toast is already shown.
      }
    },
    [
      isLoading,
      potentialCriteriaProblems,
      handleHide,
      flagFiles,
      dataKey,
      fileName,
      baseCategory,
      type,
      flagType,
      editMode,
      flagId,
      uploadDocument,
      accountId,
      updateFlag,
      addFlag,
      attachedDocumentIds,
      isDecisionRendered,
    ],
  );

  const handleResolve = useCallback(() => {
    handleHide();
    showResolveModal(dataKey, fileName, flagId);
  }, [showResolveModal, dataKey, fileName, flagId, handleHide]);

  const truncatedFileName = trimFileNames(fileName, dataKey);
  const headerTitle = editMode ? 'Edit Flag' : `${fileName ? 'Flag Item' : 'Flag Section'}: ${truncatedFileName}`;

  const getInitialValues = useMemo(() => {
    const initialPurpose = (() => {
      const current = selectedFlagToEdit?.purposeType;
      if (editMode && current && purposeOptions.some((o) => o.value === current)) return current;
      return (purposeOptions[0]?.value as FlagPurposeType) ?? FlagPurposeType.rejection;
    })();

    const baseValues = {
      ...potentialCriteriaProblems,
      purpose: initialPurpose,
      note: '',
    } as Record<string, any>;

    if (editMode && selectedFlagToEdit) {
      baseValues.note = selectedFlagToEdit.note || '';

      if (selectedFlagToEdit.reason) {
        baseValues.reason = selectedFlagToEdit.reason;
      }

      if (selectedCriterion?.value && getInitialSelectedProblems.length) {
        baseValues[selectedCriterion.value] = getInitialSelectedProblems;
      }
    }

    return baseValues;
  }, [potentialCriteriaProblems, editMode, selectedFlagToEdit, selectedCriterion, getInitialSelectedProblems]);

  return (
    <Modal
      isOpen={isOpen}
      maxWidth="5xl"
      ariaLabelledBy="addFlagHeader"
      fullScreenMobile
      overflow="auto"
      onDismiss={handleHide}
    >
      <Modal.Header id="addFlagHeader" title={headerTitle} onDismiss={handleHide} />
      <Formik initialValues={getInitialValues} validateOnChange={false} onSubmit={handleSubmit} enableReinitialize>
        {({ errors, handleBlur, isSubmitting, setFieldError, setFieldValue, values }) => (
          <Form noValidate>
            <Modal.Body background="secondary" childGap="lg">
              {flagCriteriaOrDefaultReasonsAvailable ? (
                <Box childGap="lg">
                  {setting && setting?.acceptanceCriteria?.length ? (
                    <Box gap="lg">
                      <SelectInput
                        key="selectAcceptanceCriteria"
                        id="selectAcceptanceCriteria"
                        name="selectAcceptanceCriteria"
                        label="Flag requirement"
                        value={selectedCriterion}
                        onChange={(event) => {
                          Object.keys(potentialCriteriaProblems).forEach((key) => {
                            const previousCriterionSelectionId = Object.keys(values).find(
                              (valueKey) => valueKey === key,
                            );

                            if (previousCriterionSelectionId) {
                              setFieldValue(previousCriterionSelectionId, []);
                            }
                          });
                          setSelectedCriterion(event.target.value);
                        }}
                        isDisabled={isSubmitting}
                        isRequired
                        onBlur={handleBlur}
                        options={availableCriteria?.map((criterion) => ({
                          label: criterion?.displayText,
                          value: criterion?.id.toString(),
                        }))}
                        isClearable
                        menuPortalTarget={document.body}
                      />
                      <SelectInput
                        key={selectedCriterion?.value}
                        id={selectedCriterion?.value || ''}
                        name={selectedCriterion?.value}
                        label="Issue(s)"
                        value={values[selectedCriterion?.value ?? '']}
                        onChange={(event) => {
                          setFieldError('selectAcceptanceCriteria', undefined);
                          setFieldValue(selectedCriterion?.value ?? '', event.target.value);
                          return;
                        }}
                        options={selectedCriterionOptions}
                        isDisabled={isSubmitting || !selectedCriterion}
                        isRequired
                        isMulti
                        isClearable
                        menuPortalTarget={document.body}
                      />
                    </Box>
                  ) : (
                    <Field
                      label="Reason"
                      name="reason"
                      id="reason"
                      options={reasonOptions}
                      component={FormikSelectInputNative}
                      onChange={(event: any) => {
                        setFieldError('selectAcceptanceCriteria', undefined);
                        setFieldValue('reason', event.target.value);
                        return;
                      }}
                      autoComplete="off"
                      isDisabled={isSubmitting}
                      isRequired
                    />
                  )}
                  {errors?.selectAcceptanceCriteria && (
                    <Box color="danger" fontSize="sm">
                      {errors?.selectAcceptanceCriteria as string}
                    </Box>
                  )}
                  <Box gap="xs">
                    <Field
                      type="text"
                      label="Notes"
                      name="note"
                      id="note"
                      placeholder="Add a note if this is still a flag"
                      rows="2"
                      component={FormikTextareaInput}
                      isDisabled={isSubmitting}
                    />
                    {showUploadButton && (
                      <RequirePermissions
                        permissions={[UserPermissions.admin, UserPermissions.editor]}
                        checkAllPermissions={false}
                      >
                        <Box direction="row" childGap="md" alignItems="center">
                          <Box direction="row" alignItems="center" gap="sm">
                            <ResettingFileUpload
                              labelText="Upload"
                              id={`upload-${FlagDocumentTypes.flag}-photos`}
                              name={`${FlagDocumentTypes.flag}-files`}
                              size="sm"
                              multiple
                              onChange={handleFileUpload}
                              buttonText={!isPhone ? 'Upload file(s)' : null}
                              onClick={(event: any) => event.stopPropagation()}
                              isDisabled={isLoading || isSubmitting || accountIsLoading}
                              files={flagFiles}
                            />{' '}
                            <Box fontSize="sm" color="placeholder">
                              {'image files only (jpg, png, gif)'}
                            </Box>
                          </Box>
                        </Box>
                      </RequirePermissions>
                    )}
                  </Box>

                  {editMode && attachedDocumentIds.length > 0 && (
                    <Box gap="xs">
                      <Box fontSize="sm" fontWeight="medium" color="body-secondary">
                        Attached package documents
                      </Box>
                      {attachedDocumentIds.map((docId) => (
                        <AttachedDocItem
                          key={docId}
                          documentId={docId}
                          accountId={accountId}
                          onRemove={handleRemoveAttachment}
                          isLocked={isDecisionRendered}
                        />
                      ))}
                    </Box>
                  )}

                  {flagCriteriaOrDefaultReasonsAvailable && (
                    <FlagPurposeSelectionPartial
                      purposeOptions={purposeOptions}
                      disabled={isSubmitting || accountIsLoading}
                    />
                  )}
                </Box>
              ) : (
                <Box>{`All available problem areas for creating ${flagType} flags are in use in other open flags. Resolve one to create other.`}</Box>
              )}
            </Modal.Body>
            <Modal.Footer>
              {editMode && canDeleteFlag && (
                <Button
                  onClick={handleDelete}
                  tone="danger"
                  size="md"
                  disabled={isLoading || isSubmitting || accountIsLoading}
                  variant="tertiary"
                >
                  Delete
                </Button>
              )}
              <Button
                as="button"
                onClick={handleHide}
                variant="secondary"
                tone="neutral"
                size="md"
                disabled={isLoading || isSubmitting || accountIsLoading}
              >
                Cancel
              </Button>
              {editMode && (
                <Button
                  onClick={handleResolve}
                  color="primary"
                  size="md"
                  disabled={isLoading || isSubmitting || accountIsLoading}
                  variant="secondary"
                >
                  Resolve
                </Button>
              )}
              <Button
                size="md"
                type="submit"
                color="danger"
                disabled={isLoading || isSubmitting || Boolean(errors?.selectAcceptanceCriteria) || accountIsLoading}
              >
                Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
